<script>
import { cancelOrderPayment, getOrderDetail, manualCancelOrderPayment, refundOrderPayment, updateOrderStatus } from '@/api/order.js'
import AdminShell from '@/components/admin/layout/AdminShell.vue'
import AdminStatusBadge from '@/components/admin/table/AdminStatusBadge.vue'
import AdminLoadingState from '@/components/admin/feedback/AdminLoadingState.vue'

export default {
  name: 'OrderDetailPage',
  components: { AdminShell, AdminStatusBadge, AdminLoadingState },
  data() {
    return {
      order: null,
      loading: false,
      saving: false,
      paymentSaving: false,
      statusForm: { orderStatus: '', memo: '' },
      refundDialog: false,
      selectedPayment: null,
      // 수동 결제취소 - 온넥트가 API 취소를 받지 않을 때(승인 다음 날 등) 사람이 온넥트에서 취소하고 여기서 표시한다
      manualDialog: false,
      manualPayment: null,
      manualReason: '',
      manualMemo: '',
      refundForm: {
        refundAmount: 0,
        reason: '',
        refundBankName: '',
        refundAccountNo: '',
        refundAccountHolder: '',
      },
      orderStatusOptions: [
        { title: '주문완료/입금대기', value: 'ORDERED' },
        { title: '입금만료', value: 'PAYMENT_EXPIRED' },
        { title: '입금완료', value: 'PAID' },
        { title: '상품/배송 준비', value: 'PREPARING' },
        { title: '방문수령 준비완료', value: 'READY_FOR_PICKUP' },
        { title: '배송중', value: 'SHIPPING' },
        { title: '배송완료', value: 'DELIVERED' },
        { title: '수령완료', value: 'PICKED_UP' },
        { title: '최종완료', value: 'COMPLETED' },
        { title: '취소', value: 'CANCELED' },
      ],
    }
  },
  computed: {
    orderCode() {
      return this.$route.params.orderCode
    },
    statusMap() {
      return {
        ORDERED: { label: '주문완료', tone: 'warning' },
        PAYMENT_EXPIRED: { label: '입금만료', tone: 'default' },
        PAID: { label: '입금완료', tone: 'success' },
        PREPARING: { label: '준비중', tone: 'info' },
        READY_FOR_PICKUP: { label: '수령준비완료', tone: 'info' },
        SHIPPING: { label: '배송중', tone: 'info' },
        DELIVERED: { label: '배송완료', tone: 'success' },
        PICKED_UP: { label: '수령완료', tone: 'success' },
        COMPLETED: { label: '최종완료', tone: 'success' },
        CANCELED: { label: '취소', tone: 'default' },
      }
    },
    paymentStatusMap() {
      return {
        REQUESTED: { label: '입금대기', tone: 'warning' },
        PAID: { label: '입금완료', tone: 'success' },
        FAILED: { label: '실패', tone: 'danger' },
        CANCELED: { label: '취소', tone: 'default' },
        REFUNDED: { label: '환불', tone: 'info' },
      }
    },
    cardPaymentStatusMap() {
      return {
        ...this.paymentStatusMap,
        REQUESTED: { label: '결제대기', tone: 'warning' },
        PAID: { label: '결제완료', tone: 'success' },
      }
    },
    availableStatusOptions() {
      if (!this.order) return this.orderStatusOptions
      return this.orderStatusOptions.filter((item) => {
        if (this.order.deliveryMethod === 'DELIVERY') return !['READY_FOR_PICKUP', 'PICKED_UP'].includes(item.value)
        if (this.order.deliveryMethod === 'PICKUP') return !['SHIPPING', 'DELIVERED'].includes(item.value)
        return true
      })
    },
    canEditStatus() {
      return this.order?.orderStatus !== 'CANCELED'
    },
  },
  mounted() {
    this.fetchDetail()
  },
  methods: {
    async fetchDetail() {
      this.loading = true
      try {
        const res = await getOrderDetail(this.orderCode)
        if (res.data?.code === 0) {
          this.order = res.data.data
          this.statusForm.orderStatus = this.order.orderStatus || ''
          this.statusForm.memo = ''
        } else {
          window.alert(res.data?.message || '주문 정보를 찾을 수 없습니다.')
          this.$router.push('/orders')
        }
      } catch (e) {
        console.error('[OrderDetailPage] fetchDetail error:', e)
        window.alert('주문 정보를 불러오는 중 오류가 발생했습니다.')
        this.$router.push('/orders')
      } finally {
        this.loading = false
      }
    },
    async saveStatus() {
      if (!this.statusForm.orderStatus) {
        window.alert('변경할 주문상태를 선택해주세요.')
        return
      }
      if (!window.confirm('주문상태를 변경하시겠습니까?')) return
      this.saving = true
      try {
        const res = await updateOrderStatus(this.orderCode, {
          orderStatus: this.statusForm.orderStatus,
          memo: this.statusForm.memo || undefined,
        })
        if (res.data?.code === 0) {
          await this.fetchDetail()
        } else {
          window.alert(res.data?.message || '주문상태 변경에 실패했습니다.')
        }
      } catch (e) {
        console.error('[OrderDetailPage] saveStatus error:', e)
        window.alert('주문상태 변경 중 오류가 발생했습니다.')
      } finally {
        this.saving = false
      }
    },
    canCancelPayment(payment) {
      return (payment.paymentType === 'CASH' && this.order?.orderStatus !== 'CANCELED' && payment.paymentStatus === 'REQUESTED')
        || this.canCancelCard(payment)
    },
    /** 결제완료된 카드 결제 - 온넥트 승인 취소 + 주문 · 재고 · 원장 되돌림(2026-11-02) */
    canCancelCard(payment) {
      return payment.paymentType === 'CARD'
        && payment.paymentStatus === 'PAID'
        && !['CANCELED', 'COMPLETED'].includes(this.order?.orderStatus)
    },
    cancelMethodLabel(value) {
      return {
        API_CANCELED: '온넥트 API 취소',
        MANUAL_CANCELED: '수동 결제취소',
        AUTO_CANCELED: '자동 취소',
        PROVIDER_REFUND: '온넥트에서 취소',
        PROVIDER_CANCELED: '온넥트에서 취소',
      }[value] || value || '-'
    },
    openManualDialog(payment, reason = '') {
      this.manualPayment = payment
      this.manualReason = reason
      this.manualMemo = ''
      this.manualDialog = true
    },
    async submitManualCancel() {
      if (!this.manualMemo || this.manualMemo.trim().length < 2) {
        window.alert('어디서 · 언제 취소했는지 메모를 남겨 주세요.')
        return
      }
      if (!window.confirm('온넥트에서 취소를 마쳤습니까?\n이 결제를 취소 처리하고 주문 · 재고 · 원장을 되돌립니다.')) return
      this.paymentSaving = true
      try {
        const res = await manualCancelOrderPayment(this.orderCode, {
          paymentCode: this.manualPayment.paymentCode,
          memo: this.manualMemo.trim(),
        })
        if (res.data?.code === 0) {
          this.manualDialog = false
          window.alert(res.data?.message || '수동 결제취소로 처리했습니다.')
          await this.fetchDetail()
        } else {
          window.alert(res.data?.message || '수동 결제취소에 실패했습니다.')
        }
      } catch (e) {
        console.error('[OrderDetailPage] submitManualCancel error:', e)
        window.alert('수동 결제취소 중 오류가 발생했습니다.')
      } finally {
        this.paymentSaving = false
      }
    },
    canRefundPayment(payment) {
      return this.order?.orderStatus !== 'CANCELED'
        && payment.paymentType === 'CASH'
        && payment.paymentStatus === 'PAID'
    },
    async cancelPayment(payment) {
      const card = payment.paymentType === 'CARD'
      const reason = window.prompt('결제 취소 사유를 입력해 주세요.', card ? '관리자 카드 결제 취소' : '관리자 결제 취소')
      if (reason === null) return
      const question = card
        ? '카드 승인을 취소하고 주문을 취소합니다.\n재고가 되돌아가고 재고/정산 원장이 정리됩니다. 진행하시겠습니까?'
        : '입금대기 결제를 취소하고 주문을 취소 처리하시겠습니까?'
      if (!window.confirm(question)) return
      this.paymentSaving = true
      try {
        const res = await cancelOrderPayment(this.orderCode, {
          paymentCode: payment.paymentCode,
          reason,
        })
        if (res.data?.code === 0) {
          if (card) window.alert(res.data?.message || '카드 결제를 취소했습니다.')
          await this.fetchDetail()
        } else if (res.data?.manualAvailable) {
          // 온넥트가 받지 않았다(승인 다음 날 등) - 사람이 온넥트에서 취소하고 여기서 표시한다
          this.openManualDialog(payment, res.data?.message || '')
        } else {
          window.alert(res.data?.message || '결제 취소 처리에 실패했습니다.')
        }
      } catch (e) {
        console.error('[OrderDetailPage] cancelPayment error:', e)
        window.alert('결제 취소 처리 중 오류가 발생했습니다.')
      } finally {
        this.paymentSaving = false
      }
    },
    openRefundDialog(payment) {
      this.selectedPayment = payment
      this.refundForm = {
        refundAmount: payment.paymentAmount || 0,
        reason: '관리자 환불 처리',
        refundBankName: '',
        refundAccountNo: '',
        refundAccountHolder: '',
      }
      this.refundDialog = true
    },
    async submitRefund() {
      if (!this.selectedPayment) return
      if (!this.refundForm.refundAmount || Number(this.refundForm.refundAmount) <= 0) {
        window.alert('환불 금액을 입력해 주세요.')
        return
      }
      if (!window.confirm('환불 완료로 처리하고 주문을 취소 상태로 변경하시겠습니까?')) return
      this.paymentSaving = true
      try {
        const res = await refundOrderPayment(this.orderCode, {
          paymentCode: this.selectedPayment.paymentCode,
          refundAmount: Number(this.refundForm.refundAmount),
          reason: this.refundForm.reason,
          refundBankName: this.refundForm.refundBankName,
          refundAccountNo: this.refundForm.refundAccountNo,
          refundAccountHolder: this.refundForm.refundAccountHolder,
        })
        if (res.data?.code === 0) {
          this.refundDialog = false
          await this.fetchDetail()
        } else {
          window.alert(res.data?.message || '환불 처리에 실패했습니다.')
        }
      } catch (e) {
        console.error('[OrderDetailPage] submitRefund error:', e)
        window.alert('환불 처리 중 오류가 발생했습니다.')
      } finally {
        this.paymentSaving = false
      }
    },
    goBack() {
      this.$router.push('/orders')
    },
    formatAmount(value) {
      return `${Number(value || 0).toLocaleString()}원`
    },
    formatDate(value) {
      return value ? String(value).substring(0, 10) : '-'
    },
    formatDateTime(value) {
      return value ? String(value).substring(0, 16).replace('T', ' ') : '-'
    },
    deliveryLabel(value) {
      return value === 'PICKUP' ? '방문수령' : value === 'DELIVERY' ? '배송수령' : value || '-'
    },
    paymentLabel(value) {
      return value === 'CASH' ? '계좌이체' : value === 'CARD' ? '카드' : value || '-'
    },
  },
}
</script>

<template>
  <AdminShell title="주문 상세">
    <template #header-actions>
      <v-btn variant="outlined" prepend-icon="mdi-arrow-left" @click="goBack">목록</v-btn>
    </template>

    <div v-if="loading" class="admin-page-card pa-6">
      <AdminLoadingState text="주문 정보를 불러오는 중..." />
    </div>

    <template v-else-if="order">
      <div class="detail-grid">
        <div>
          <div class="admin-page-card pa-5 mb-5">
            <div class="d-flex align-center justify-space-between mb-4">
              <div>
                <div class="text-h6 font-weight-bold">{{ order.orderCode }}</div>
                <div class="text-caption text-medium-emphasis">{{ order.orderGroupCode || '-' }}</div>
              </div>
              <AdminStatusBadge :status="order.orderStatus" :map="statusMap" size="small" />
            </div>
            <div class="summary-grid">
              <div><span>회원</span><strong>{{ order.userName || '-' }}</strong></div>
              <div><span>이메일</span><strong>{{ order.userEmail || '-' }}</strong></div>
              <div><span>연락처</span><strong>{{ order.userPhone || '-' }}</strong></div>
              <div><span>주문금액</span><strong>{{ formatAmount(order.totalAmount) }}</strong></div>
              <div><span>수령방식</span><strong>{{ deliveryLabel(order.deliveryMethod) }}</strong></div>
              <div><span>결제수단</span><strong>{{ paymentLabel(order.paymentType) }}</strong></div>
              <div><span>주문일시</span><strong>{{ formatDateTime(order.orderedDt) }}</strong></div>
              <div><span>수정일시</span><strong>{{ formatDateTime(order.updatedDt) }}</strong></div>
            </div>
          </div>

          <div class="admin-page-card pa-5 mb-5">
            <div class="text-subtitle-1 font-weight-bold mb-3">주문 상품</div>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>상품명</th>
                  <th class="text-center">지점</th>
                  <th class="text-center">수량</th>
                  <th class="text-center">중량</th>
                  <th class="text-end">판매가</th>
                  <th class="text-end">임가공비</th>
                  <th class="text-end">금액</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in order.items || []" :key="item.orderItemCode">
                  <td>
                    <div class="font-weight-medium">{{ item.productName }}</div>
                    <div class="text-caption text-medium-emphasis">{{ item.productCode }}</div>
                  </td>
                  <td class="text-center">{{ item.branchName || item.branchCode || '-' }}</td>
                  <td class="text-center">{{ item.quantity }}</td>
                  <td class="text-center">{{ item.weight }}g</td>
                  <td class="text-end">{{ formatAmount(item.salePrice) }}</td>
                  <td class="text-end">{{ formatAmount(item.processingFee) }}</td>
                  <td class="text-end font-weight-bold">{{ formatAmount(item.itemAmount) }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <div class="admin-page-card pa-5 mb-5">
            <div class="text-subtitle-1 font-weight-bold mb-3">결제 정보</div>
            <v-alert
              v-if="order.cancelRequest"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              회원 취소 요청 · {{ formatDateTime(order.cancelRequest.requestedDt) }} · {{ order.cancelRequest.reason || '-' }}<br />
              바로 취소되지 않아 요청으로 접수됐습니다(승인 다음 날이거나 온넥트 취소 실패). '결제 취소'를 먼저 눌러 보고,
              안 되면 온넥트 담당자에게 요청한 뒤 '수동 결제취소'로 처리해 주세요. 처리 전에는 다음 단계로 넘길 수 없습니다.
            </v-alert>
            <div v-for="payment in order.payments || []" :key="payment.paymentCode" class="payment-row">
              <div>
                <div class="font-weight-medium">{{ paymentLabel(payment.paymentType) }} {{ formatAmount(payment.paymentAmount) }}</div>
                <div class="text-caption text-medium-emphasis">{{ payment.paymentCode }}</div>
                <div v-if="payment.card" class="depositor-box mt-2">
                  <div class="text-caption text-medium-emphasis">카드 승인</div>
                  <div class="text-body-2 font-weight-medium mt-1">
                    승인번호 {{ payment.card.approvalNo || '-' }}
                    · {{ payment.card.issuer || '-' }} {{ payment.card.cardLast4 ? '****' + payment.card.cardLast4 : '' }}
                  </div>
                  <div class="text-caption text-medium-emphasis mt-1">
                    거래번호 {{ payment.card.trxId || '-' }} · 승인 {{ formatDateTime(payment.card.approvedDt) }}
                  </div>
                  <template v-if="payment.card.checkoutStatus === 'CANCELED'">
                    <div class="text-caption mt-2">
                      취소 {{ formatDateTime(payment.card.canceledDt) }} · {{ cancelMethodLabel(payment.card.cancelMethod) }}
                      <span v-if="payment.card.canceledBy"> · {{ payment.card.canceledBy }}</span>
                    </div>
                    <div v-if="payment.card.cancelMemo" class="text-caption text-medium-emphasis mt-1">{{ payment.card.cancelMemo }}</div>
                    <div class="text-caption mt-1" :class="payment.card.refundNotifiedDt ? 'text-success' : 'text-warning'">
                      {{ payment.card.refundNotifiedDt
                        ? '온넥트 취소 확인 · ' + formatDateTime(payment.card.refundNotifiedDt)
                        : '온넥트 취소 통보 대기 - 오지 않으면 온넥트에서 거래 상태를 확인해 주세요' }}
                    </div>
                  </template>
                </div>
                <div v-if="payment.paymentType === 'CASH'" class="text-caption mt-1">
                  {{ payment.bankName || '-' }} {{ payment.accountNo || '-' }} {{ payment.accountHolder || '' }}
                </div>
                <div v-if="payment.paymentType === 'CASH'" class="depositor-box mt-2">
                  <div class="text-caption text-medium-emphasis">입금자 정보</div>
                  <div class="text-body-2 font-weight-medium mt-1">
                    {{ payment.depositorName || '-' }}
                  </div>
                  <div class="text-caption text-medium-emphasis mt-1">
                    {{ payment.depositorBankName || '-' }} {{ payment.depositorAccountNo || '-' }}
                  </div>
                </div>
              </div>
              <div class="payment-actions">
                <AdminStatusBadge
                  :status="payment.paymentStatus"
                  :map="payment.paymentType === 'CARD' ? cardPaymentStatusMap : paymentStatusMap"
                  size="x-small"
                />
                <v-btn
                  v-if="canCancelPayment(payment)"
                  size="small"
                  variant="outlined"
                  color="error"
                  :loading="paymentSaving"
                  @click="cancelPayment(payment)"
                >
                  {{ payment.paymentType === 'CARD' ? '결제 취소' : '입금대기 취소' }}
                </v-btn>
                <v-btn
                  v-if="canCancelCard(payment)"
                  size="small"
                  variant="text"
                  color="error"
                  :loading="paymentSaving"
                  @click="openManualDialog(payment)"
                >
                  수동 결제취소
                </v-btn>
                <v-btn
                  v-if="canRefundPayment(payment)"
                  size="small"
                  variant="outlined"
                  color="warning"
                  :loading="paymentSaving"
                  @click="openRefundDialog(payment)"
                >
                  환불 처리
                </v-btn>
              </div>
            </div>
          </div>

          <div class="admin-page-card pa-5 mb-5">
            <div class="text-subtitle-1 font-weight-bold mb-3">결제 취소/환불 내역</div>
            <div v-if="(order.refunds || []).length === 0" class="text-body-2 text-medium-emphasis py-4">
              환불 처리 내역이 없습니다.
            </div>
            <div v-for="refund in order.refunds || []" :key="refund.refundCode" class="refund-row">
              <div>
                <div class="font-weight-medium">{{ formatAmount(refund.refundAmount) }} / {{ refund.refundMethod }}</div>
                <div class="text-caption text-medium-emphasis">{{ refund.refundCode }} · {{ refund.paymentCode }}</div>
                <div class="text-caption text-medium-emphasis mt-1">{{ refund.claimReason || '-' }}</div>
                <div v-if="refund.refundBankName || refund.refundAccountNo" class="text-caption text-medium-emphasis mt-1">
                  {{ refund.refundBankName || '-' }} {{ refund.refundAccountNo || '-' }} {{ refund.refundAccountHolder || '' }}
                </div>
              </div>
              <div class="text-right">
                <AdminStatusBadge :status="refund.refundStatus" :map="paymentStatusMap" size="x-small" />
                <div class="text-caption text-medium-emphasis mt-2">{{ formatDateTime(refund.refundedDt || refund.requestedDt) }}</div>
                <div class="text-caption text-medium-emphasis">{{ refund.processorName || refund.processorEmail || '-' }}</div>
              </div>
            </div>
          </div>

          <div v-if="order.deliveryMethod === 'DELIVERY'" class="admin-page-card pa-5 mb-5">
            <div class="text-subtitle-1 font-weight-bold mb-3">배송 정보</div>
            <div v-if="order.shipment" class="summary-grid">
              <div><span>수령인</span><strong>{{ order.shipment.receiverName || '-' }}</strong></div>
              <div><span>연락처</span><strong>{{ order.shipment.receiverPhone || '-' }}</strong></div>
              <div><span>주소</span><strong>{{ order.shipment.address1 || '-' }} {{ order.shipment.address2 || '' }}</strong></div>
              <div><span>배송상태</span><strong>{{ order.shipment.shipmentStatus || '-' }}</strong></div>
              <div><span>배송메모</span><strong>{{ order.shipment.deliveryMemo || '-' }}</strong></div>
              <div><span>배송완료</span><strong>{{ formatDateTime(order.shipment.deliveredDt) }}</strong></div>
            </div>
          </div>

          <div v-if="order.deliveryMethod === 'PICKUP'" class="admin-page-card pa-5 mb-5">
            <div class="text-subtitle-1 font-weight-bold mb-3">방문수령 정보</div>
            <div class="summary-grid">
              <div><span>수령지점</span><strong>{{ order.receiptBranchName || order.receiptBranchCode || '-' }}</strong></div>
              <div><span>수령예정일</span><strong>{{ formatDate(order.expectedReceiptDate) }}</strong></div>
            </div>
          </div>

          <div class="admin-page-card pa-5">
            <div class="text-subtitle-1 font-weight-bold mb-3">상태 변경 히스토리</div>
            <div v-if="(order.histories || []).length === 0" class="text-body-2 text-medium-emphasis py-4">
              상태 변경 이력이 없습니다.
            </div>
            <div v-for="history in order.histories || []" :key="history.orderLogCode" class="history-row">
              <div class="history-dot" />
              <div class="history-body">
                <div class="d-flex align-center justify-space-between flex-wrap ga-2">
                  <AdminStatusBadge :status="history.logReason || '-'" :map="statusMap" size="x-small" />
                  <span class="text-caption text-medium-emphasis">{{ formatDateTime(history.createdDt) }}</span>
                </div>
                <div class="text-body-2 mt-2">
                  처리자:
                  <strong>{{ history.operatorName || history.operatorEmail || history.operatorCode || '-' }}</strong>
                </div>
                <div v-if="history.logDetail" class="text-caption text-medium-emphasis mt-1">
                  {{ history.logDetail }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="admin-page-card pa-5 sticky-panel">
            <div class="text-subtitle-1 font-weight-bold mb-4">주문상태 변경</div>
            <v-alert v-if="order.orderStatus === 'CANCELED'" type="info" variant="tonal" class="mb-4">
              취소된 주문은 상태를 변경할 수 없습니다.
            </v-alert>
            <v-select
              v-model="statusForm.orderStatus"
              :items="availableStatusOptions"
              item-title="title"
              item-value="value"
              label="주문상태"
              variant="outlined"
              density="comfortable"
              :disabled="!canEditStatus"
            />
            <v-textarea
              v-model="statusForm.memo"
              label="변경 메모"
              variant="outlined"
              rows="4"
              auto-grow
              :disabled="!canEditStatus"
            />
            <v-btn block color="primary" prepend-icon="mdi-check" :loading="saving" :disabled="!canEditStatus" @click="saveStatus">
              상태 저장
            </v-btn>
            <div class="text-caption text-medium-emphasis mt-3">
              입금완료로 변경하면 계좌이체 결제도 입금완료 처리됩니다.
            </div>
          </div>
        </div>
      </div>
    </template>

    <v-dialog v-model="manualDialog" max-width="560">
      <div class="admin-page-card pa-5">
        <div class="text-h6 font-weight-bold mb-3">수동 결제취소</div>
        <v-alert v-if="manualReason" type="error" variant="tonal" density="compact" class="mb-3">
          {{ manualReason }}
        </v-alert>
        <div class="text-body-2 mb-3">
          온넥트 쪽 취소는 사람이 먼저 마칩니다 - 승인 당일이면 온넥트 가맹점 사이트에서, 그 뒤는 온넥트 담당자에게 요청합니다.
          취소를 마친 뒤 메모를 남기고 처리하면 <strong>이 주문의 결제 · 주문 · 재고 · 원장만</strong> 되돌립니다.
          온넥트 취소 통보가 오면 결제 정보에 '온넥트 취소 확인' 이 붙습니다.
        </div>
        <v-textarea
          v-model="manualMemo"
          label="메모 (필수) - 어디서 · 언제 · 누가 취소했는지"
          variant="outlined"
          rows="3"
          auto-grow
          hide-details="auto"
        />
        <div class="d-flex justify-end ga-2 mt-4">
          <v-btn variant="text" @click="manualDialog = false">닫기</v-btn>
          <v-btn color="error" :loading="paymentSaving" @click="submitManualCancel">수동 결제취소 처리</v-btn>
        </div>
      </div>
    </v-dialog>

    <v-dialog v-model="refundDialog" max-width="520">
      <div class="admin-page-card pa-5">
        <div class="text-h6 font-weight-bold mb-4">환불 처리</div>
        <v-text-field
          v-model.number="refundForm.refundAmount"
          label="환불 금액"
          type="number"
          variant="outlined"
          density="comfortable"
        />
        <v-text-field
          v-model="refundForm.refundBankName"
          label="환불 은행"
          variant="outlined"
          density="comfortable"
        />
        <v-text-field
          v-model="refundForm.refundAccountNo"
          label="환불 계좌번호"
          variant="outlined"
          density="comfortable"
        />
        <v-text-field
          v-model="refundForm.refundAccountHolder"
          label="예금주"
          variant="outlined"
          density="comfortable"
        />
        <v-textarea
          v-model="refundForm.reason"
          label="환불 사유"
          variant="outlined"
          rows="3"
          auto-grow
        />
        <div class="d-flex justify-end ga-2">
          <v-btn variant="text" @click="refundDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="paymentSaving" @click="submitRefund">환불 완료 처리</v-btn>
        </div>
      </div>
    </v-dialog>
  </AdminShell>
</template>

<style scoped>
.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 20px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.summary-grid span {
  display: block;
  color: rgba(203, 213, 225, 0.72);
  font-size: 12px;
  margin-bottom: 4px;
}

.summary-grid strong {
  color: #f8fafc;
  font-size: 14px;
}

.payment-row,
.refund-row,
.history-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 0;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
}

.payment-row,
.refund-row {
  justify-content: space-between;
}

.payment-row:first-of-type,
.refund-row:first-of-type,
.history-row:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.payment-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.depositor-box {
  display: inline-block;
  min-width: 220px;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.2);
}

.history-dot {
  width: 9px;
  height: 9px;
  margin-top: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  flex: 0 0 auto;
}

.history-body {
  flex: 1;
  min-width: 0;
}

.sticky-panel {
  position: sticky;
  top: 84px;
}

@media (max-width: 1100px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .sticky-panel {
    position: static;
  }
}

@media (max-width: 720px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
